import { useEffect, useMemo } from "react"
import { Box, SxProps, Theme, TooltipProps } from "@mui/material"
import { OfflineBolt } from "@mui/icons-material"
import { combineStatuses, ERROR, OK } from "helpers/data-statuses"
import { getEligibleModels, getMostUsedOrDefaultModelId } from "helpers/models"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "connected-components/dashboard/context/dashboard-id"
import { getCoverageResourceFromInstrument } from "connected-components/dashboard/helpers"
import Tooltip from "components/material/tooltip"
import { useDashboardModelsWithCoverage } from "../../hooks/use-dashboard-models-with-coverage"
import { useCoreActions } from "../../hooks/use-core-actions"
import ModelSelectorAutocomplete from "connected-components/model-selector-autocomplete"

const MODEL_SELECTOR_WRAPPER_SX: SxProps<Theme> = { display: "flex", alignItems: "center" }
const WARNING_ICON_SX: SxProps<Theme> = { width: 17, height: 17, mr: 0.5 }
const WARNING_TOOLTIP_SLOT_PROPS: TooltipProps["slotProps"] = { tooltip: { sx: { maxWidth: 250 } } }

export default function ModelSelector({ disabled = false, onModelChange, warningMessage }: ModelSelectorProps) {
  const dashboardId = useDashboardId()
  const modelId = useAppSelector((s) => s.dashboard.model[dashboardId]?.modelId ?? null)
  const instrument = useAppSelector((s) => s.dashboard.core[dashboardId]?.instrument ?? null)
  const { setModelId } = useCoreActions()

  const { modelsWithCoverage, status, requiresResourceCoverage, portfolios, portfoliosStatus } =
    useDashboardModelsWithCoverage(instrument, modelId)

  useEffect(
    () => {
      // Wait for the coverage gate itself (not just models/portfolios) so the default pick can
      // land on a covered model when coverage is knowable up front (etf/index/security) instead
      // of racing ahead of it — mergeModelsWithCoverage always treats whatever gets selected here
      // as covered, so picking before coverage resolves silently skips the gate this PR added.
      if (status === OK && portfoliosStatus === OK && !modelId && modelsWithCoverage) {
        const defaultModelId = getMostUsedOrDefaultModelId(
          portfolios ?? [],
          getEligibleModels(modelsWithCoverage, requiresResourceCoverage),
        )
        if (defaultModelId) {
          setModelId(defaultModelId)
        }
      }
    },
    [modelId, setModelId, portfolios, portfoliosStatus, modelsWithCoverage, status, requiresResourceCoverage],
  )

  const handleModelChange = onModelChange || setModelId

  const combinedStatus = useMemo(
    () => combineStatuses([status, portfoliosStatus]),
    [status, portfoliosStatus],
  )

  const coverageResource = useMemo(() => getCoverageResourceFromInstrument(instrument), [instrument])

  if (combinedStatus === ERROR) {
    return null
  }

  if (combinedStatus !== OK || !modelsWithCoverage) {
    return <div>Loading</div>
  }

  return <Box sx={MODEL_SELECTOR_WRAPPER_SX}>
    {warningMessage && (
      <Tooltip title={warningMessage} slotProps={WARNING_TOOLTIP_SLOT_PROPS}>
        <OfflineBolt sx={WARNING_ICON_SX} aria-label="FX hedge unsupported" />
      </Tooltip>
    )}
    <ModelSelectorAutocomplete
      models={modelsWithCoverage}
      value={modelId || ""}
      onChange={handleModelChange}
      disabled={disabled}
      requiresResourceCoverage={requiresResourceCoverage}
      resourceTypeLabel={coverageResource?.resourceType === "swap" ? "basket" : coverageResource?.resourceType}
      popperPlacement="bottom-end"
    />
  </Box>
}

interface ModelSelectorProps {
  disabled?: boolean
  onModelChange?: (modelId: string) => void
  warningMessage?: string
}
